/**
 * Changer de contexte (projet, programme, perspective) — LE seul chemin (FND-V050-185).
 *
 * Le contexte vit à deux endroits : les fournisseurs (`useWorkspace`, `useProgram`, persistés) et
 * l'URL (`?projet=`, `?programme=`, `?perspective=`, N4/N5), que `BarreContexte` synchronise.
 * Changer l'un sans l'autre est un défaut : un écran qui appelait `setActiveId` seul (carte du
 * tableau de bord, création de projet, onboarding) laissait `?projet=` sur l'ANCIEN projet, et la
 * synchronisation URL → état le rétablissait à la navigation suivante. Ici, un changement écrit
 * TOUJOURS les deux : les fournisseurs d'abord (l'écran suit tout de suite), puis l'URL — la
 * barre ne relit l'URL que lorsqu'elle CHANGE, donc l'ancienne adresse, encore affichée pendant la
 * transition du routeur, ne peut plus rien rétablir.
 *
 * Les fonctions rendues sont STABLES et lisent l'état le plus récent (références) : elles restent
 * justes dans un `onSuccess` de mutation appelé après des `await`, où la fermeture est périmée.
 */
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useProgramOptionnel } from '@/hooks/useProgram'
import { useWorkspace } from '@/hooks/useWorkspace'
import { ecrireContexteUrl, lireContexteUrl, type ContexteUrl } from '@/lib/contexteUrl'

export interface OptionsChangement {
  /**
   * Écran d'arrivée (chemin, avec ou sans `?…`). Absent : on reste sur l'écran courant et ses
   * autres paramètres (`?vue=`, `?section=`) sont conservés. Présent et différent : on part de ses
   * propres paramètres, seule la perspective voyage.
   */
  vers?: string | undefined
  /** `push` (défaut) pour un choix de l'utilisateur ; `replace` pour un alignement automatique. */
  mode?: 'push' | 'replace'
}

export interface ChangerContexte {
  /** Applique un delta de contexte (URL + fournisseurs). `null` retire un niveau. */
  changerContexte: (delta: Partial<ContexteUrl>, options?: OptionsChangement) => void
  /**
   * Rend `id` projet actif. Le programme suit le parent du projet, sauf s'il est donné (un projet
   * qui vient d'être créé n'est pas encore dans la liste) ; parent inconnu ⇒ aucun programme.
   */
  choisirProjet: (id: string, options?: OptionsChangement & { programme?: string | null }) => void
}

export function useChangerContexte(): ChangerContexte {
  const { pathname, search, hash } = useLocation()
  const navigate = useNavigate()
  const espaces = useWorkspace()
  const programmes = useProgramOptionnel()

  const courant = useRef({ pathname, search, hash, espaces, programmes, navigate })
  useLayoutEffect(() => {
    courant.current = { pathname, search, hash, espaces, programmes, navigate }
  })

  const changerContexte = useCallback((delta: Partial<ContexteUrl>, options: OptionsChangement = {}): void => {
    const c = courant.current
    if (delta.projet !== undefined && delta.projet !== c.espaces.activeId) c.espaces.setActiveId(delta.projet)
    if (delta.programme !== undefined && c.programmes && delta.programme !== c.programmes.activeId) {
      c.programmes.setActiveId(delta.programme)
    }
    const [cheminVers = c.pathname, rechercheVers] = (options.vers ?? c.pathname).split(/(?=\?)/, 2)
    const memeEcran = cheminVers === c.pathname && rechercheVers === undefined
    const base = memeEcran
      ? c.search
      : ecrireContexteUrl(rechercheVers ?? '', { perspective: lireContexteUrl(c.search).perspective })
    const suivant = ecrireContexteUrl(base, delta)
    if (suivant !== c.search || cheminVers !== c.pathname) {
      // Même écran : le fragment (#ancre) de l'adresse courante reste (FND-V050-195) ; un autre
      // écran part de sa propre adresse, sans l'ancre de l'écran quitté.
      c.navigate({ pathname: cheminVers, search: suivant, hash: memeEcran ? c.hash : '' },
        { replace: options.mode === 'replace' })
    }
  }, [])

  const choisirProjet = useCallback((id: string, options: OptionsChangement & { programme?: string | null } = {}): void => {
    const { programme, ...reste } = options
    const parent = programme !== undefined
      ? programme
      : courant.current.espaces.workspaces.find((w) => w.workspace_id === id)?.program_id ?? null
    changerContexte({ projet: id, programme: parent }, reste)
  }, [changerContexte])

  return useMemo(() => ({ changerContexte, choisirProjet }), [changerContexte, choisirProjet])
}
